import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { CheckCircle2, Clock, Info, Loader2, ScrollText, ShieldCheck } from 'lucide-react';
import { getLegalReviewQueue } from '../services/api/implementation/impl';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import LegalFlowStrip from '../components/postSales/LegalFlowStrip';
import { Callout, Tile } from '../../shared/components/ui/primitives';
import {
  canRecordLegalVerdict,
  LEGAL_STATUS,
  legalStatusClasses,
  legalStatusLabel
} from '../../shared/utils/legalAccess';

/**
 * Open work is fetched once and split into tabs on the client, so the counts on
 * the tabs are real rather than a guess. Cleared work is a separate query
 * because it lives behind a closed round and is a record, not a worklist.
 */
/* Same chip as ManagementHeaderTabs. */
const headerTabClass = (active) =>
  `whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium border transition-all duration-200 shrink-0 ${
    active
      ? 'bg-[#2A565E] text-white border-[#2A565E]'
      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
  }`;

const OPEN_TABS = [
  { id: 'all', label: 'All open', match: () => true },
  { id: LEGAL_STATUS.SUBMITTED, label: 'New' },
  { id: LEGAL_STATUS.IN_REVIEW, label: 'In review' },
  { id: LEGAL_STATUS.QUERY_RAISED, label: 'With Sales' },
  { id: LEGAL_STATUS.REOPENED, label: 'Reopened' }
];

const EMPTY_COPY = {
  all: 'Nothing waiting on Legal right now.',
  [LEGAL_STATUS.SUBMITTED]: 'No new deals have been submitted.',
  [LEGAL_STATUS.IN_REVIEW]: 'Nothing is part-way through review.',
  [LEGAL_STATUS.QUERY_RAISED]: 'No deals are sitting with Sales.',
  [LEGAL_STATUS.REOPENED]: 'Nothing has been reopened.',
  cleared: 'No deals have been cleared yet.'
};

const dayLabel = (days) =>
  days == null ? '—' : days === 0 ? 'Today' : `${days} day${days === 1 ? '' : 's'}`;

const dateLabel = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    : '—';

const LegalQueue = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  const session = {
    vertical: Cookies.get('userVertical'),
    level: Cookies.get('userDesignationLevel'),
    role: Cookies.get('userRole')
  };
  const allowed = canRecordLegalVerdict(session);
  const showingCleared = tab === 'cleared';

  const openQuery = useQuery({
    queryKey: ['legalQueue', 'open'],
    queryFn: () => getLegalReviewQueue({ limit: 100 }),
    enabled: allowed
  });

  const clearedQuery = useQuery({
    queryKey: ['legalQueue', 'cleared'],
    queryFn: () => getLegalReviewQueue({ status: LEGAL_STATUS.LEGAL_VERIFIED, limit: 50 }),
    enabled: allowed && showingCleared
  });

  const openRows = useMemo(() => openQuery.data?.rows ?? [], [openQuery.data]);

  const counts = useMemo(() => {
    const byStatus = { all: openRows.length };
    OPEN_TABS.forEach((t) => {
      if (t.id === 'all') return;
      byStatus[t.id] = openRows.filter((r) => r.status === t.id).length;
    });
    return byStatus;
  }, [openRows]);

  const rows = useMemo(() => {
    if (showingCleared) return clearedQuery.data?.rows ?? [];
    const filtered = tab === 'all' ? openRows : openRows.filter((r) => r.status === tab);
    return unassignedOnly ? filtered.filter((r) => !r.assigned_to) : filtered;
  }, [showingCleared, clearedQuery.data, openRows, tab, unassignedOnly]);

  const oldest = useMemo(
    () => openRows.reduce((max, r) => Math.max(max, r.days_waiting ?? 0), 0),
    [openRows]
  );

  if (!allowed) {
    return (
      <Layout>
        <Header data={{ name: 'Legal queue' }}>
        {/* Queue tabs in the header, drawn like the Operations / Management /
            Workforce tabs (green chip) so Legal reads as the same product. */}
        <div
          className="flex items-center gap-2 overflow-x-auto"
          role="tablist"
          aria-label="Queue">
          {[
            ...OPEN_TABS.map((t) => ({
              id: t.id,
              label: `${t.label} (${String(counts[t.id] ?? 0).padStart(2, '0')})`
            })),
            { id: 'cleared', label: 'Cleared' }
          ].map((t) => {
            const active = showingCleared ? t.id === 'cleared' : t.id === tab;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                className={headerTabClass(active)}>
                {t.label}
              </button>
            );
          })}
        </div>
      </Header>
        <div className="p-4 text-sm text-gray-600">
          The legal queue is for the Legal and Management verticals.
        </div>
      </Layout>
    );
  }

  const loading = showingCleared ? clearedQuery.isLoading : openQuery.isLoading;
  const errored = showingCleared ? clearedQuery.isError : openQuery.isError;

  return (
    <Layout>
      <Header data={{ name: 'Legal queue' }}>
        {/* Queue tabs in the header, drawn like the Operations / Management /
            Workforce tabs (green chip) so Legal reads as the same product. */}
        <div
          className="flex items-center gap-2 overflow-x-auto"
          role="tablist"
          aria-label="Queue">
          {[
            ...OPEN_TABS.map((t) => ({
              id: t.id,
              label: `${t.label} (${String(counts[t.id] ?? 0).padStart(2, '0')})`
            })),
            { id: 'cleared', label: 'Cleared' }
          ].map((t) => {
            const active = showingCleared ? t.id === 'cleared' : t.id === tab;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                className={headerTabClass(active)}>
                {t.label}
              </button>
            );
          })}
        </div>
      </Header>

      <div className="p-4 sm:p-4">
      <header className="mb-5">
        <Callout
          tone="info"
          collapsible
          icon={<Info size={14} />}
          title="About the legal queue"
          className="sm:max-w-2xl">
          Closed deals waiting on legal verification, oldest first. Verifying a deal
          releases its priorities and invoices; Management countersigning is optional.
        </Callout>
        <div className="mt-3">
          <LegalFlowStrip squared status={LEGAL_STATUS.SUBMITTED} compact />
        </div>
      </header>

      <div className="mb-5 grid gap-3 sm:grid-cols-3 sm:gap-4">
        {/* The Company page's card, same as HR and Labour. */}
        <Tile
          variant="card"
          icon={<ScrollText size={18} />}
          label="Open with Legal"
          value={counts.all ?? 0}
          tone="brand"
        />
        <Tile
          variant="card"
          icon={<Clock size={18} />}
          label="Longest waiting"
          value={dayLabel(oldest)}
          tone={oldest >= 7 ? 'warning' : 'neutral'}
        />
        <Tile
          variant="card"
          icon={<CheckCircle2 size={18} />}
          label="Back with Sales"
          value={counts[LEGAL_STATUS.QUERY_RAISED] ?? 0}
          tone="info"
        />
      </div>

      {!showingCleared ? (
        <div className="mb-4 flex justify-end">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={unassignedOnly}
              onChange={(e) => setUnassignedOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-brand-600"
            />
            Unassigned only
          </label>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading queue…
          </div>
        ) : errored ? (
          <div className="py-10 text-center text-sm text-danger-fg">
            Could not load the legal queue. Refresh to try again.
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-sm text-gray-500">
            <ScrollText className="h-8 w-8 text-gray-300" aria-hidden />
            {EMPTY_COPY[showingCleared ? 'cleared' : tab] || EMPTY_COPY.all}
            {!showingCleared && tab === 'all' ? (
              <span className="text-xs text-gray-400">
                Deals appear here once Sales submits them for verification.
              </span>
            ) : null}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[780px] text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 font-medium">Deal</th>
                  <th className="px-4 py-3 font-medium">Applicant</th>
                  <th className="px-4 py-3 font-medium">Unit</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">
                    {showingCleared ? 'Verified' : 'Waiting'}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {showingCleared ? 'Management' : 'Assigned'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    tabIndex={0}
                    onClick={() => row.lead_id && navigate(`/builder/sales/${row.lead_id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && row.lead_id) {
                        navigate(`/builder/sales/${row.lead_id}`);
                      }
                    }}
                    className="cursor-pointer border-b border-gray-50 last:border-0 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-brand-600">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      #{row.deal_id}
                      {row.round > 1 ? (
                        <span className="ml-1.5 text-[11px] font-normal text-gray-400">
                          round {row.round}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{row.applicant_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{row.unit_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${legalStatusClasses(
                          row.status
                        )}`}>
                        {legalStatusLabel(row.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-700">
                      {showingCleared ? (
                        <span title={row.legal_verified_by_name || ''}>
                          {dateLabel(row.legal_verified_at)}
                        </span>
                      ) : (
                        <span
                          className={
                            (row.days_waiting ?? 0) >= 7 ? 'font-semibold text-warning-fg' : ''
                          }>
                          {dayLabel(row.days_waiting)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {showingCleared ? (
                        row.management_approved ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-success-bg px-2.5 py-1 text-[11px] font-semibold text-success-fg ring-1 ring-success-border">
                            <ShieldCheck className="h-3 w-3" aria-hidden />
                            Approved{row.approved_by_name ? ` · ${row.approved_by_name}` : ''}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Not countersigned</span>
                        )
                      ) : (
                        row.assignee_name || (
                          <span className="text-xs text-gray-400">Unassigned</span>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </Layout>
  );
};

export default LegalQueue;
